/**
 * AlertBell Component
 * 
 * A notification bell component for the header that displays the count of unread notifications
 * and uses color coding based on the highest severity notification.
 * 
 * Features:
 * - Displays unread notification count as a badge
 * - Color-coded based on highest severity notification
 * - Click handler to toggle notification list
 * - Animation for new notifications
 * - Accessibility compliant with proper ARIA attributes
 * 
 * @module AlertBell
 */

import React, { useState, useEffect, useRef } from 'react';
import NotificationService from '../../services/NotificationService';

/**
 * AlertBell component for displaying notification status in the header
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onClick - Function to call when the bell is clicked
 * @returns {JSX.Element} AlertBell component
 */
function AlertBell({ onClick }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [highestSeverity, setHighestSeverity] = useState('Info');
  const [animate, setAnimate] = useState(false);
  const notificationService = useRef(new NotificationService());
  const previousUnreadCount = useRef(0);

  /**
   * Update the unread count and highest severity
   */
  const updateNotificationStatus = async () => {
    try {
      // Get unread count
      const count = await notificationService.current.getUnreadCount();
      
      // Get all notifications to determine highest severity
      const notifications = await notificationService.current.getUserNotifications();
      const unreadNotifications = notifications.filter(n => !n.read);
      
      // Determine highest severity
      let severity = 'Info';
      if (unreadNotifications.some(n => n.severity === 'Critical')) {
        severity = 'Critical';
      } else if (unreadNotifications.some(n => n.severity === 'Warning')) {
        severity = 'Warning';
      }
      
      // Update state
      setUnreadCount(count);
      setHighestSeverity(severity);
      
      // Trigger animation if unread count increased
      if (count > previousUnreadCount.current) {
        setAnimate(true);
        // Reset animation after it completes
        setTimeout(() => setAnimate(false), 1000);
      }
      
      previousUnreadCount.current = count;
    } catch (error) {
      console.error('Error updating notification status:', error);
    }
  };

  // Initial load and periodic refresh
  useEffect(() => {
    // Update on initial load
    updateNotificationStatus();
    
    // Set up periodic refresh (every 10 seconds)
    const intervalId = setInterval(updateNotificationStatus, 10000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Subscribe to notification events
  useEffect(() => {
    // Create a BroadcastChannel for notification updates
    const channel = new BroadcastChannel('notification-updates');
    
    // Listen for notification updates
    const handleNotificationUpdate = () => {
      updateNotificationStatus();
    };
    
    channel.addEventListener('message', handleNotificationUpdate);
    
    // Clean up event listener on unmount
    return () => {
      channel.removeEventListener('message', handleNotificationUpdate);
      channel.close();
    };
  }, []);

  /**
   * Get CSS class based on severity
   * @returns {string} CSS class for severity
   */
  const getSeverityClass = () => {
    switch (highestSeverity) {
      case 'Critical':
        return 'severity-critical';
      case 'Warning':
        return 'severity-warning';
      case 'Info':
      default:
        return 'severity-info';
    }
  };

  return (
    <div 
      className={`alert-bell-container ${animate ? 'animate' : ''}`}
      onClick={onClick}
      role="button"
      aria-label={`Notifications: ${unreadCount} unread`}
      tabIndex="0"
      onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClick();
            }
          }}
    >
      <div className={`alert-bell ${getSeverityClass()}`}>
        <i className="bell-icon" aria-hidden="true"></i>
        {unreadCount > 0 && (
          <span className="unread-badge" aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
    </div>
  );
}

export default AlertBell;
